/**
 * AWS Amplify v6 Error Handling Utilities
 *
 * In Amplify v6, error handling has changed significantly from previous versions.
 * This module provides proper error handling utilities compatible with Amplify v6.
 */

// Define our own AuthError interface to avoid import issues
interface AmplifyAuthError {
  name?: string
  message?: string
  code?: string
  __type?: string
  statusCode?: number
  status?: number
}

/**
 * Error types that can occur in Amplify v6
 */
export enum AmplifyErrorType {
  Network = 'NetworkError',
  NotAuthenticated = 'NotAuthenticatedException',
  TokenExpired = 'TokenExpiredException',
  UserNotFound = 'UserNotFoundException',
  InvalidPassword = 'InvalidPasswordException',
  CodeMismatch = 'CodeMismatchException',
  ExpiredCode = 'ExpiredCodeException',
  LimitExceeded = 'LimitExceededException',
  TooManyRequests = 'TooManyRequestsException',
  Unknown = 'UnknownError'
}

/**
 * Check if an error is an Amplify AuthError
 * In Amplify v6, we check for error properties instead of instanceof
 */
export function isAuthError (error: unknown): error is AmplifyAuthError {
  if (!error || typeof error !== 'object') return false

  const err = error as any

  // Check for Amplify v6 auth error characteristics
  return (
    err.__type || // Amplify v6 error marker
    err.name?.includes('Exception') ||
    err.name?.includes('Error') ||
    (err.message && typeof err.message === 'string' && (
      err.message.includes('Cognito') ||
      err.message.includes('Authentication') ||
      err.message.includes('Authorization')
    ))
  )
}

/**
 * Check if an error is a network error
 * In Amplify v6, network errors typically manifest as failed promises
 * with specific error messages or when the error has no response
 */
export function isNetworkError (error: unknown): boolean {
  if (!error) return false

  // Type guard for error object
  const err = error as any

  // Check if it's an AuthError with network-related issues
  if (isAuthError(error)) {
    const message = error.message?.toLowerCase() ?? ''
    const name = error.name?.toLowerCase() ?? ''

    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      name.includes('network')
    )
  }

  // Check for standard network error patterns
  if (err.message) {
    const lowerMessage = err.message.toLowerCase()
    const networkPatterns = [
      'network error',
      'failed to fetch',
      'network request failed',
      'fetch failed',
      'unable to connect',
      'connection refused',
      'connection reset',
      'connection timeout',
      'no internet',
      'offline'
    ]

    if (networkPatterns.some(pattern => lowerMessage.includes(pattern))) {
      return true
    }
  }

  // Check for specific error codes
  if (err.code) {
    const networkCodes = [
      'NetworkError',
      'NetworkingError',
      'ENOTFOUND',
      'ECONNREFUSED',
      'ECONNRESET',
      'ETIMEDOUT',
      'ENETUNREACH'
    ]

    if (networkCodes.includes(err.code)) {
      return true
    }
  }

  // Check if it's a TypeError from fetch (common network error)
  if (err.name === 'TypeError' && err.message?.includes('fetch')) {
    return true
  }

  // Check for Amplify-specific network error patterns
  if (err.underlyingError) {
    return isNetworkError(err.underlyingError)
  }

  return false
}

/**
 * Check if an error is retryable
 */
export function isRetryableError (error: unknown): boolean {
  if (!error) return false

  // Network errors are typically retryable
  if (isNetworkError(error)) {
    return true
  }

  const err = error as any

  // Check for specific retryable error names
  const retryableNames = [
    'ThrottlingException',
    'TooManyRequestsException',
    'RequestThrottled',
    'ServiceUnavailable',
    'RequestTimeout',
    'ProvisionedThroughputExceededException'
  ]

  if (err.name && retryableNames.includes(err.name)) {
    return true
  }

  // Check for retryable HTTP status codes
  if (err.statusCode || err.status || err.response?.status) {
    const statusCode = err.statusCode ?? err.status ?? err.response?.status
    const retryableStatusCodes = [ 408, 429, 500, 502, 503, 504 ]

    if (retryableStatusCodes.includes(statusCode)) {
      return true
    }
  }

  // Check for Amplify AuthError retryable conditions
  if (isAuthError(error)) {
    return error.name === 'LimitExceededException' ||
           error.name === 'TooManyRequestsException'
  }

  return false
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage (error: unknown): string {
  if (!error) return 'An unknown error occurred'

  if (isAuthError(error)) {
    // Map common auth errors to user-friendly messages
    switch (error.name) {
      case 'UserNotFoundException':
        return 'Invalid email or password'
      case 'NotAuthorizedException':
        return 'Invalid email or password'
      case 'InvalidPasswordException':
        return 'Password does not meet requirements'
      case 'CodeMismatchException':
        return 'Invalid verification code'
      case 'ExpiredCodeException':
        return 'Verification code has expired'
      case 'LimitExceededException':
        return 'Too many attempts. Please try again later'
      case 'TooManyRequestsException':
        return 'Too many requests. Please wait and try again'
      default:
        return error.message ?? 'Authentication error'
    }
  }

  if (isNetworkError(error)) {
    return 'Network connection error. Please check your internet connection and try again.'
  }

  const err = error as any
  return err.message ?? 'An error occurred'
}

/**
 * Enhanced error handler for Amplify operations
 */
export async function handleAmplifyError<T> (
  operation: () => Promise<T>,
  options?: {
    retries?: number
    retryDelay?: number
    onRetry?: (attempt: number, error: unknown) => void
  }
): Promise<T> {
  const maxRetries = options?.retries ?? 3
  const retryDelay = options?.retryDelay ?? 1000

  let lastError: unknown

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation()
    } catch (error) {
      lastError = error

      if (!isRetryableError(error) || attempt === maxRetries) {
        throw error
      }

      if (options?.onRetry) {
        options.onRetry(attempt, error)
      }

      // Exponential backoff
      await new Promise(resolve =>
        setTimeout(resolve, retryDelay * Math.pow(2, attempt - 1))
      )
    }
  }

  throw lastError
}

/**
 * Create a global error handler for unhandled promise rejections
 * This helps catch any Amplify errors that might not be properly handled
 */
export function setupGlobalErrorHandler (): void {
  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason

      if (isAuthError(error) || isNetworkError(error)) {
        console.error('Unhandled Amplify error:', error)

        // You can dispatch Redux actions or show toast notifications here
        // For network errors, you might want to show an offline indicator
        if (isNetworkError(error)) {
          console.warn('Network error detected - user may be offline')
        }

        // Prevent the default error handling
        event.preventDefault()
      }
    })
  }
}

// Make isNetworkError globally available for any legacy AWS SDK code
if (typeof window !== 'undefined') {
  // Add to window for global access
  (window as any).isNetworkError = isNetworkError;
  (window as any).isRetryableError = isRetryableError

  // Try to patch into various possible locations where AWS SDK might look
  const patchLocations = [
    window,
    (window as any).AWS,
    (window as any).AWSAmplify,
    (window as any).Amplify
  ]

  patchLocations.forEach(location => {
    if (location && typeof location === 'object') {
      if (!location.isNetworkError) {
        Object.defineProperty(location, 'isNetworkError', {
          value: isNetworkError,
          writable: false,
          configurable: true
        })
      }
      if (!location.isRetryableError) {
        Object.defineProperty(location, 'isRetryableError', {
          value: isRetryableError,
          writable: false,
          configurable: true
        })
      }
    }
  })

  // Specifically handle the 'l' object that the minified code uses
  Object.defineProperty(window, 'l', {
    get () {
      return {
        isNetworkError,
        isRetryableError,
        ...(this._l || {})
      }
    },
    set (value) {
      this._l = {
        ...value,
        isNetworkError,
        isRetryableError
      }
    },
    configurable: true
  })
}

// Export for backward compatibility
export default {
  isAuthError,
  isNetworkError,
  isRetryableError,
  getErrorMessage,
  handleAmplifyError,
  setupGlobalErrorHandler
}
